import { useEffect, useRef, useState } from "react";
import type { NfcClockResult } from "../../api/nfcSchemas";
import type { MyProjects, Project } from "../../api/schemas";
import { activitiesForProject, choosableActivity } from "../../domain/activities";
import type { ActivityOption } from "../../domain/activities";
import type { ClockState } from "../../domain/clock";
import { currentAttribution, openShiftStart, toggleFavorite } from "../../domain/projects";
import type { CurrentAttribution } from "../../domain/projects";
import { parseInstant, toEpochSeconds, toLocalDate } from "../../domain/time";
import type { EpochSeconds, LocalDate } from "../../domain/time";
import type { WorkDay } from "../../domain/workday";
import {
  buildLocalTimeline,
  currentWorkDay,
  localClockState,
  localOpenDaysToCorrect,
} from "../../offline/localTimeline";
import type { LocalTimeline } from "../../offline/localTimeline";
import type {
  ClockSnapshot,
  LocalAttribution,
  LocalEvent,
  ProjectPreferences,
} from "../../offline/model";
import {
  useClockSnapshot,
  useLocalAttributions,
  useLocalEvents,
  useOffline,
  useProjectCache,
  useProjectPreferences,
} from "../../offline/OfflineContext";
import { isOwnEvent } from "../../offline/ownEvents";
import {
  recordProjectRange,
  recordProjectSwitch,
  rememberProjectUse,
} from "../../offline/recordAttribution";
import { recordClockEvent } from "../../offline/recordEvent";
import { useSync } from "../../offline/SyncContext";
import { useOnlineStatus } from "../../offline/useOnlineStatus";
import { usePurchasePaths } from "../../platform/PlatformContext";
import { useI18n } from "../../shared/i18n/I18nContext";
import { useOwnerKey } from "../../shared/session/tenantSelection";
import { useMe } from "../../shared/session/useMe";
import { useWorkTimeZone } from "../../shared/session/useWorkTimeZone";
import { useAnnouncer } from "../../shared/ui/Announcer";
import { describeErrorCode } from "../../shared/ui/describeError";
import { formatTime, formatTimeOrDateTime } from "../../shared/ui/format";
import { useNow } from "../../shared/ui/useNow";
import { clockActions } from "./model/clockActions";
import type { ClockAction } from "./model/clockActions";
import { limitsFor } from "./model/clockLimits";
import type { ClockLimits } from "./model/clockLimits";
import { clockStepShown } from "./model/clockStep";
import type { ClockStep } from "./model/clockStep";
import { computeClockWarnings, earliestRegularStart } from "./model/clockWarnings";
import type { ClockWarning } from "./model/clockWarnings";
import { useLocationCapture } from "./useLocationCapture";
import type { LocationCaptureControl } from "./useLocationCapture";
import { useNfcClock } from "./useNfcClock";
import type { NfcClockControl } from "./useNfcClock";

/** A step waiting for the information on the location capture (T-175). */
interface PendingNotice {
  /** The step. */
  readonly action: ClockAction;
  /** Whether the person confirmed an early start before the information appeared. */
  readonly earlyStartConfirmed: boolean;
}

/** Options of {@link useClockControl}. */
export interface ClockControlOptions {
  /** Interval in milliseconds at which the live durations and warnings are computed again. */
  readonly tickMs: number;
  /**
   * Called after a clock step was stored on the device, after its announcement and after the
   * location was added (T-175), and after a step the server accepted at an NFC tag (F-78).
   */
  readonly onRecorded?: ((action: ClockAction) => void) | undefined;
  /**
   * Called instead of opening the day overview here after a clock-out under the requirement
   * „Abfrage beim Ausstempeln“ (spec 6.5.4, P-2), for example by the compact clock, which leads to
   * the start page for it.
   */
  readonly onDayOverview?: ((date: LocalDate) => void) | undefined;
  /** Date whose day overview is open from the start, for example after the compact clock led here. */
  readonly initialOverviewDate?: LocalDate | null | undefined;
}

/** The clock of the signed-in person: state, warnings, actions and projects (F-75). */
export interface ClockControl {
  /** Owner key of the signed-in person and tenant. */
  readonly owner: string;
  /** The current instant. */
  readonly now: EpochSeconds;
  /** The current instant in milliseconds. */
  readonly nowMs: number;
  /** Today in {@link timeZone}. */
  readonly today: LocalDate;
  /** Time zone of the employment, or of the device without one (F-18). */
  readonly timeZone: string;
  /** Statutory limits of today (adults or minors). */
  readonly limits: ClockLimits;
  /** Last known state of the server, or `null` if none is stored. */
  readonly snapshot: ClockSnapshot | null;
  /** Local timeline of the server state and the local events. */
  readonly timeline: LocalTimeline;
  /** Clock state and the instant it began. */
  readonly clock: { readonly state: ClockState; readonly since: EpochSeconds | null };
  /** The current working day, or `null`. */
  readonly day: WorkDay | null;
  /** Earliest regular start after the rest period, or `null`. */
  readonly earliestStart: EpochSeconds | null;
  /** Live warnings, most urgent first. */
  readonly warnings: readonly ClockWarning[];
  /** Whether the daily rest period is still running. */
  readonly restActive: boolean;
  /** Own events the server rejected. */
  readonly conflicts: readonly LocalEvent[];
  /** Whether own events wait to be sent. */
  readonly pendingOwn: boolean;
  /** Every action of the state, in the order the screens offer them. */
  readonly actions: readonly ClockAction[];
  /** The next steps of the day (Kommen, Ruhepause, Weiter). */
  readonly primaryActions: readonly ClockAction[];
  /** Standby and waiting time, if offered. */
  readonly standbyAction: ClockAction | undefined;
  /** „Gehen“, if offered. */
  readonly clockOutAction: ClockAction | undefined;
  /** Label of an action. */
  readonly actionLabel: (action: ClockAction) => string;
  /** The state as a sentence („Eingestempelt seit 08:02“). */
  readonly statusText: string;
  /** Formats an instant, with its date when it is not today (F-17). */
  readonly timeText: (instant: EpochSeconds) => string;
  /**
   * Whether a clock step is being stored or stored but its result not shown yet, or a project change
   * is being stored. The buttons stay busy until the screen shows the step; a tap meanwhile is
   * ignored (F-104).
   */
  readonly busy: boolean;
  /** The error of the last step, or `null`. */
  readonly recordError: string | null;
  /** Whether a clock-in during the rest period waits for its confirmation. */
  readonly confirmEarlyStart: boolean;
  /** Closes the confirmation of an early start without recording. */
  readonly cancelEarlyStart: () => void;
  /** Location capture of the employer (T-175). */
  readonly location: LocationCaptureControl;
  /** The step waiting for the information on the location capture, or `null`. */
  readonly noticeAction: ClockAction | null;
  /** Remembers the information and records the waiting step with the location. */
  readonly confirmLocationNotice: () => void;
  /** Records the waiting step without the location. */
  readonly recordWithoutLocation: () => void;
  /** Closes the information without recording. */
  readonly dismissLocationNotice: () => void;
  /** Starts a step: confirmation of an early start and the location information come first. */
  readonly onAction: (action: ClockAction) => void;
  /**
   * Steps at a workplace NFC tag (T-174): available only on a device with NFC, online and while
   * the employer offers it; the start page and the compact clock show them alike (F-78).
   */
  readonly nfc: NfcClockControl;
  /**
   * Follows a step the server accepted at a tag like a step recorded here: announcement, day
   * overview under „Abfrage beim Ausstempeln“, {@link ClockControlOptions.onRecorded} and the
   * synchronization.
   */
  readonly nfcRecorded: (action: ClockAction, result: NfcClockResult) => void;
  /** Projects of the person, `null` before the first synchronization. */
  readonly myProjects: MyProjects | null;
  /** Projects that can be chosen now (none while project time tracking is off). */
  readonly projects: readonly Project[];
  /** Favorites and recently used projects. */
  readonly projectChoices: ProjectPreferences;
  /** Projects with „Budget fast erschöpft“. */
  readonly nearlyExhausted: ReadonlySet<string>;
  /** The project for the next clock-in, or `null`. */
  readonly clockInProjectId: string | null;
  /** Chooses the project for the next clock-in. */
  readonly chooseClockInProject: (projectId: string | null) => void;
  /** The activity for the next clock-in, or `null`. */
  readonly clockInActivity: string | null;
  /** Chooses the activity for the next clock-in. */
  readonly chooseClockInActivity: (activityId: string | null) => void;
  /** The activities that can be chosen for a project. */
  readonly activitiesOf: (projectId: string | null) => readonly ActivityOption[];
  /** Whether the requirement asks for an activity (a hint, never blocking). */
  readonly requireActivity: boolean;
  /** The project and activity that apply now. */
  readonly current: CurrentAttribution;
  /** Switches the project (and activity) while working. */
  readonly switchProject: (projectId: string | null, activityId?: string | null) => void;
  /** Marks or unmarks a favorite project. */
  readonly toggleFavoriteProject: (projectId: string) => void;
  /** Local project switches, for the day overview. */
  readonly attributions: readonly LocalAttribution[];
  /** Date of the open day overview, or `null`. */
  readonly overviewDate: LocalDate | null;
  /** The working day of the open day overview, if known. */
  readonly overviewDay: WorkDay | undefined;
  /** Closes the day overview. */
  readonly closeOverview: () => void;
  /** Attributes a range of the day overview to a project. */
  readonly assignRange: (
    start: EpochSeconds,
    end: EpochSeconds,
    projectId: string | null,
    activityId: string | null,
  ) => void;
}

/**
 * The one implementation of clocking for the signed-in person (F-75): the start page „Heute“ and the
 * compact clock of the app bar both use it, so warnings (rest period, minors, Sundays and public
 * holidays through the forecast, F-53), the confirmation of an early start, the location information
 * (T-175), the project for the clock-in (T-124, T-184) and the day overview (T-125) behave the same
 * everywhere. Steps go to the outbox of the device (`guardedWrite` inside the recording functions),
 * so they work offline; feedback goes through `useAnnouncer` (F-61).
 *
 * @param options - Options.
 * @returns The clock, or `null` while the local state loads or nobody is signed in.
 */
export function useClockControl(options: ClockControlOptions): ClockControl | null {
  const { t, languageTag } = useI18n();
  const purchasePaths = usePurchasePaths();
  const { announce } = useAnnouncer();
  const offline = useOffline();
  const { syncNow } = useSync();
  const me = useMe();
  // The employment's time zone, before `GET /me` answers the one stored with the last profile (F-18).
  const workTimeZone = useWorkTimeZone();
  const owner = useOwnerKey();
  const localEvents = useLocalEvents(owner);
  const snapshot = useClockSnapshot(owner);
  const nowMs = useNow(offline.nowMs, options.tickMs);
  const now = toEpochSeconds(nowMs);
  const [confirmEarlyStart, setConfirmEarlyStart] = useState(false);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // F-104: the clock step whose result the screen does not show yet, as state for `busy` and,
  // synchronously, as a ref for the taps. A second tap before the re-render would be checked against
  // the state before the first step and store the same step twice.
  const [writingStep, setWritingStep] = useState<ClockStep | null>(null);
  const stepRef = useRef<ClockStep | null>(null);
  const attributions = useLocalAttributions(owner);
  const projectCache = useProjectCache(owner);
  const projectChoices = useProjectPreferences(owner);
  const online = useOnlineStatus();
  const [clockInProject, setClockInProject] = useState<string | null | undefined>(undefined);
  const [clockInActivity, setClockInActivity] = useState<string | null>(null);
  const [overviewDate, setOverviewDate] = useState<LocalDate | null>(
    options.initialOverviewDate ?? null,
  );
  // T-175: the location when clocking, only while the employer captures it, with the information first.
  const location = useLocationCapture(owner);
  // T-174: steps at a workplace NFC tag, only on a device with NFC and while the employer offers it.
  const nfc = useNfcClock(owner);
  const [pendingNotice, setPendingNotice] = useState<PendingNotice | null>(null);
  const noticeAction = pendingNotice?.action ?? null;
  // The answer to the location information of a clock-in that then had to wait for the confirmation
  // of an early start: `true` with the location, `false` without, `null` if none was given (F-78).
  const [earlyStartNotice, setEarlyStartNotice] = useState<boolean | null>(null);

  const timeZone = workTimeZone ?? offline.deviceTimeZone;
  const today = toLocalDate(now, timeZone);
  const limits = limitsFor(me.data?.minorUntil ?? null, today);
  // Recomputed at every tick anyway (live durations); the projection is linear in the events.
  const timeline = buildLocalTimeline(snapshot ?? null, localEvents.events, now, timeZone);
  const clock = localClockState(timeline);
  const stepShown =
    writingStep !== null && clockStepShown(writingStep, clock.state, localEvents.events);

  // The render that shows the step ends `busy` at once; the state is cleared during rendering, so a
  // step whose event is pruned later never holds the buttons again.
  if (stepShown) {
    setWritingStep(null);
  }

  const stepBusy = writingStep !== null && !stepShown;

  // A step whose result the screen shows no longer holds back further taps.
  useEffect(() => {
    const step = stepRef.current;

    if (step !== null && clockStepShown(step, clock.state, localEvents.events)) {
      stepRef.current = null;
    }
  }, [clock.state, localEvents.events]);

  if (owner === null || !localEvents.loaded || snapshot === undefined) {
    return null;
  }

  const day = currentWorkDay(timeline, today, timeZone);
  const serverEarliest =
    snapshot?.clockState.earliestRegularStart == null
      ? null
      : parseInstant(snapshot.clockState.earliestRegularStart);
  const earliestStart = earliestRegularStart(timeline, serverEarliest, limits.minRestSeconds, {
    now,
    limits: limits.adjoiningRest,
  });
  const earlierOpenDays = localOpenDaysToCorrect(snapshot, timeline, today, now, timeZone);
  const warnings = computeClockWarnings({
    state: clock.state,
    day,
    now,
    limits,
    earliestStart,
    earlierOpenDays,
  });
  const restActive = warnings.some((warning) => warning.kind === "restActive");
  const conflicts = localEvents.events.filter(
    (event) => event.syncStatus === "rejected" && isOwnEvent(event),
  );
  const pendingOwn = localEvents.events.some(
    (event) => event.syncStatus === "pending" && isOwnEvent(event),
  );
  const myProjects = projectCache?.myProjects ?? null;
  const projects =
    myProjects === null || myProjects.requirement.mode === "Disabled" ? [] : myProjects.projects;
  const projectIds = new Set(projects.map((project) => project.id));
  const nearlyExhausted = new Set(myProjects?.budgetNearlyExhaustedProjectIds ?? []);
  const clockInProjectId =
    clockInProject === undefined
      ? (projectChoices.recent.find((id) => projectIds.has(id)) ?? null)
      : clockInProject;
  const activitiesOf = (projectId: string | null): readonly ActivityOption[] =>
    activitiesForProject(myProjects, projectId);
  const requireActivity = myProjects?.requirement.requireActivity === true;
  const current = currentAttribution({
    shiftStart: openShiftStart(timeline.days),
    now,
    localSwitches: attributions,
    serverBlocks: (snapshot?.projectDays ?? []).flatMap((projectDay) =>
      projectDay.blocks.map((block) => ({
        kind: block.kind,
        start: parseInstant(block.start),
        end: parseInstant(block.end),
        projectId: block.projectId,
        isOpen: block.isOpen,
        activityId: block.activityId ?? null,
      })),
    ),
    localBlocks: timeline.days.flatMap((workDay) => workDay.blocks),
  });
  const promptsAtClockOut = myProjects?.requirement.mode === "PromptAtClockOut";

  const actionLabel = (action: ClockAction): string => {
    const label =
      action.breakQualification === null
        ? t(`clock.action.${action.kind}`)
        : t(`clock.breakAction.${action.breakQualification}`);

    return label;
  };

  // The day overview appears only after the clock-out is stored (spec 6.5.4, P-2).
  const followUpRecorded = (action: ClockAction): void => {
    if (action.kind !== "ClockOut" || !promptsAtClockOut) {
      return;
    }

    const date = day?.date ?? today;

    if (options.onDayOverview === undefined) {
      setOverviewDate(date);
    } else {
      options.onDayOverview(date);
    }
  };

  // Read in event handlers only: whether the result of the last step is not shown by this render yet.
  const stepPending = (): boolean => {
    const step = stepRef.current;
    const pending = step !== null && !clockStepShown(step, clock.state, localEvents.events);

    return pending;
  };

  const releaseStep = (step: ClockStep): void => {
    if (stepRef.current === step) {
      stepRef.current = null;
    }
  };

  const record = async (action: ClockAction, withLocation: boolean): Promise<void> => {
    if (stepPending()) {
      return;
    }

    const { kind } = action;
    const step: ClockStep = { fromState: clock.state, eventId: null };
    stepRef.current = step;
    setWritingStep(step);
    setRecordError(null);
    const projectId =
      kind === "ClockIn" && projectIds.has(clockInProjectId ?? "") ? clockInProjectId : null;
    let result: Awaited<ReturnType<typeof recordClockEvent>>;

    try {
      result = await recordClockEvent(offline, {
        ownerSubject: owner,
        kind,
        currentState: clock.state,
        timeZoneId: timeZone,
        source: offline.eventSource,
        projectId,
        breakQualification: action.breakQualification,
        awaitLocation: withLocation,
      });

      if (!result.ok) {
        releaseStep(step);
        setWritingStep((current) => (current === step ? null : current));
      } else {
        // Held until the screen shows the stored event or the state it leads to.
        const recorded: ClockStep = { ...step, eventId: result.value.id };

        if (stepRef.current === step) {
          stepRef.current = recorded;
        }

        setWritingStep((current) => (current === step ? recorded : current));
      }

      if (result.ok && projectId !== null) {
        await rememberProjectUse(offline.store, owner, projectId);
        const activityId = choosableActivity(myProjects, projectId, clockInActivity);

        // T-184: the activity of the clock-in is a switch at the same instant, sent only after the server accepted the
        // clock-in and discarded when it rejects it; the clock event stays as it is.
        if (activityId !== null) {
          await recordProjectSwitch(offline, {
            ownerSubject: owner,
            projectId,
            activityId,
            occurredAt: result.value.occurredAt,
            followsEventId: result.value.id,
            source: offline.eventSource,
            offline: !online,
          });
        }
      }
    } catch {
      // The device did not store the step (for example while signing out): it is not recorded (F-57).
      // A clock event stored before the failure keeps holding further taps until it is shown.
      releaseStep(step);
      setWritingStep((current) => (current === step ? null : current));
      setRecordError(t("clock.notStored"));
      return;
    }

    // Success as a toast at the top of the content area, always in view and letting clicks through
    // (F-61); the domain error by its code, never the English developer text (UX-004, UX-107).
    if (result.ok) {
      announce(
        t("clock.recorded", {
          action: actionLabel(action),
          time: formatTime(result.value.occurredAt, languageTag, timeZone),
        }),
      );
      // T-175: the event is stored first; the location follows and is added while it waits to be sent.
      // What follows the step comes after it, so „Standort wird ermittelt“ stays in view (F-75).
      if (withLocation) {
        await location.attachTo(result.value.id);
      }

      followUpRecorded(action);
      options.onRecorded?.(action);
      void syncNow();
    } else {
      setRecordError(describeErrorCode(result.error.code, t, purchasePaths));
    }
  };

  // A step at a tag is recorded by the server at once (no outbox, no location, ADR-0144); what
  // follows it is the same as after a step recorded here.
  const nfcRecorded = (action: ClockAction, result: NfcClockResult): void => {
    announce(
      t("nfc.clock.recorded", {
        action: actionLabel(action),
        time: formatTime(parseInstant(result.occurredAt), languageTag, timeZone),
        tag: result.tagName,
      }),
    );
    followUpRecorded(action);
    options.onRecorded?.(action);
    void syncNow();
  };

  const switchProject = async (
    projectId: string | null,
    activityId: string | null = null,
  ): Promise<void> => {
    setBusy(true);
    setRecordError(null);
    let attribution: Awaited<ReturnType<typeof recordProjectSwitch>>;

    try {
      attribution = await recordProjectSwitch(offline, {
        ownerSubject: owner,
        projectId,
        activityId,
        source: offline.eventSource,
        offline: !online,
      });
    } catch {
      // Not stored on the device (for example while signing out): the switch is not recorded (F-57).
      setRecordError(t("clock.notStored"));
      return;
    } finally {
      setBusy(false);
    }

    announce(
      t("project.switched", {
        time: formatTime(attribution.occurredAt ?? now, languageTag, timeZone),
      }),
    );
    void syncNow();
  };

  const assignRange = async (
    start: EpochSeconds,
    end: EpochSeconds,
    projectId: string | null,
    activityId: string | null,
  ): Promise<void> => {
    setBusy(true);
    setRecordError(null);

    try {
      await recordProjectRange(offline, {
        ownerSubject: owner,
        projectId,
        activityId,
        start,
        end,
        source: offline.eventSource,
        offline: !online,
      });
    } catch {
      setRecordError(t("clock.notStored"));
      return;
    } finally {
      setBusy(false);
    }

    announce(t("dayOverview.saved"));
    void syncNow();
  };

  const onAction = (action: ClockAction): void => {
    if (stepPending()) {
      return;
    }

    if (action.kind === "ClockIn" && restActive && !confirmEarlyStart) {
      setConfirmEarlyStart(true);
      return;
    }

    // An answer to the location information given before the early start was asked counts once.
    const noticeAnswer = confirmEarlyStart ? earlyStartNotice : null;
    setConfirmEarlyStart(false);
    setEarlyStartNotice(null);

    // The information under Art. 13 GDPR comes before the first capture (T-175).
    if (location.policy !== null && !location.acknowledged && noticeAnswer === null) {
      setPendingNotice({ action, earlyStartConfirmed: confirmEarlyStart });
      return;
    }

    void record(action, location.policy !== null && noticeAnswer !== false);
  };

  // A step waiting for the location information is checked against the rest period again before it
  // is recorded: the rest may have begun meanwhile, and only a confirmed early start passes (F-75).
  const recordFromNotice = (withLocation: boolean): void => {
    const pending = pendingNotice;
    setPendingNotice(null);

    if (pending === null) {
      return;
    }

    // The answer to the information is kept, so the confirmed early start records without asking it
    // again (F-78).
    if (pending.action.kind === "ClockIn" && restActive && !pending.earlyStartConfirmed) {
      setEarlyStartNotice(withLocation);
      setConfirmEarlyStart(true);

      if (withLocation) {
        void location.acknowledge();
      }

      return;
    }

    if (withLocation) {
      void location.acknowledge().then(() => record(pending.action, true));
    } else {
      void record(pending.action, false);
    }
  };

  // The large button holds the next step of the day (Kommen, Ruhepause, Weiter). Standby follows
  // as a secondary action (rest break first, T-150), and "Gehen" ends the day, set apart from the
  // other actions (UX-003).
  const actions = clockActions(clock.state);
  const primaryActions = actions.filter(
    (action) => action.breakQualification !== "Standby" && action.kind !== "ClockOut",
  );
  const standbyAction = actions.find((action) => action.breakQualification === "Standby");
  const clockOutAction = actions.find((action) => action.kind === "ClockOut");

  // A time of another day names its date, so "seit 22:00" or "Stand vom Server: 23:58" is not
  // read as today after midnight (F-11, F-17).
  const timeText = (instant: EpochSeconds): string =>
    formatTimeOrDateTime(instant, today, languageTag, timeZone);
  const statusText =
    clock.state === "Off" || clock.since === null
      ? t("clock.state.Off")
      : t(
          timeline.breakQualification === "Standby"
            ? "clock.state.OnStandby"
            : `clock.state.${clock.state}`,
          { time: timeText(clock.since) },
        );

  const control: ClockControl = {
    owner,
    now,
    nowMs,
    today,
    timeZone,
    limits,
    snapshot,
    timeline,
    clock,
    day,
    earliestStart,
    warnings,
    restActive,
    conflicts,
    pendingOwn,
    actions,
    primaryActions,
    standbyAction,
    clockOutAction,
    actionLabel,
    statusText,
    timeText,
    busy: busy || stepBusy,
    recordError,
    confirmEarlyStart,
    cancelEarlyStart: () => {
      setConfirmEarlyStart(false);
      setEarlyStartNotice(null);
    },
    location,
    noticeAction,
    confirmLocationNotice: () => {
      recordFromNotice(true);
    },
    recordWithoutLocation: () => {
      recordFromNotice(false);
    },
    dismissLocationNotice: () => {
      setPendingNotice(null);
    },
    onAction,
    nfc,
    nfcRecorded,
    myProjects,
    projects,
    projectChoices,
    nearlyExhausted,
    clockInProjectId,
    chooseClockInProject: (projectId) => {
      setClockInProject(projectId);
      setClockInActivity(null);
    },
    clockInActivity,
    chooseClockInActivity: setClockInActivity,
    activitiesOf,
    requireActivity,
    current,
    switchProject: (projectId, activityId = null) => {
      void switchProject(projectId, activityId);
    },
    toggleFavoriteProject: (projectId) => {
      void offline.store.putProjectPreferences(toggleFavorite(projectChoices, projectId));
    },
    attributions,
    overviewDate,
    overviewDay:
      overviewDate === null ? undefined : timeline.days.find((item) => item.date === overviewDate),
    closeOverview: () => {
      setOverviewDate(null);
    },
    assignRange: (start, end, projectId, activityId) => {
      void assignRange(start, end, projectId, activityId);
    },
  };

  return control;
}
